// services/gazette.service.ts
import { GazetteAnnouncement, GazetteFilters, PaginatedResponse, FetchHTMLResponse } from '../types/gazette.types';

const API_BASE_URL = 'https://apis.vehidhi.com/vehidhiserver/public';

export class GazetteService {
  private static instance: GazetteService;
  private cache: Map<string, { data: GazetteAnnouncement[]; timestamp: number }> = new Map();
  private CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  static getInstance(): GazetteService {
    if (!GazetteService.instance) {
      GazetteService.instance = new GazetteService();
    }
    return GazetteService.instance;
  }

  async fetchAnnouncements(filters: GazetteFilters): Promise<PaginatedResponse> {
    const cacheKey = this.generateCacheKey(filters);
    const cached = this.cache.get(cacheKey);

    if (cached && Date.now() - cached.timestamp < this.CACHE_DURATION) {
      return {
        success: true,
        data: cached.data,
        nextCursor: filters.page + 1,
        hasMore: cached.data.length === 10,
      };
    }

    try {
      const queryParams = new URLSearchParams();
      queryParams.append('page', String(filters.page));

      if (filters.type) queryParams.append('type', filters.type);
      if (filters.office) queryParams.append('office', filters.office);
      if (filters.category) queryParams.append('category', filters.category);
      if (filters.startDate) queryParams.append('start-date', filters.startDate);
      if (filters.endDate) queryParams.append('end-date', filters.endDate);
      if (filters.openOnly) queryParams.append('open-only', '1');
      if (filters.searchTerm) queryParams.append('q', filters.searchTerm);

      const targetUrl = `https://www.gazette.gov.mv/iulaan?${queryParams.toString()}`;
      
      // Call your API endpoint
      const response = await fetch(`${API_BASE_URL}/api/fetch-html`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify({
          url: targetUrl
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: FetchHTMLResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch HTML');
      }

      const parsedData = this.parseHTML(result.html, filters.page);

      // Cache the result
      this.cache.set(cacheKey, {
        data: parsedData,
        timestamp: Date.now(),
      });

      return {
        success: true,
        data: parsedData,
        nextCursor: parsedData.length === 10 ? filters.page + 1 : null,
        hasMore: parsedData.length === 10,
      };
    } catch (error) {
      console.error('Error fetching announcements:', error);
      return {
        success: false,
        data: [],
        nextCursor: null,
        hasMore: false,
        error: error instanceof Error ? error.message : 'Failed to fetch announcements',
      };
    }
  }

  private parseHTML(html: string, page: number): GazetteAnnouncement[] {
    // Split by the pattern that separates each announcement card
    const items = html.split('col-md-12 bordered items').slice(1);

    return items
      .map((block) => {
        const type = this.extract(block, /class="iulaan-type"[^>]*>([\s\S]*?)<\/a>/);
        const office = this.extract(block, /class="iulaan-office"[^>]*>([\s\S]*?)<\/a>/);
        const title = this.extract(block, /class="iulaan-title"[^>]*>([\s\S]*?)<\/a>/);
        const link = this.extract(block, /class="iulaan-title"[^>]*href="([^"]+)"/);
        const date = this.extract(block, /ތާރީޚު:\s*([\s\S]*?)<\/div>/);
        const deadline = this.extract(block, /ސުންގަޑި:\s*([\s\S]*?)<\/div>/);
        const postedAgo = this.extract(block, /class="info"[^>]*>([\s\S]*?)<\/span>/);

        return {
          page,
          type: this.clean(type),
          office: this.clean(office),
          title: this.clean(title),
          link: this.clean(link),
          date: this.clean(date),
          deadline: this.clean(deadline),
          postedAgo: this.clean(postedAgo),
        };
      })
      .filter((i) => i.title); // Remove empty items
  }

  private extract(text: string, regex: RegExp): string {
    const match = text.match(regex);
    return match ? match[1] : '';
  }

  private clean(text: string): string {
    return text
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  private generateCacheKey(filters: GazetteFilters): string {
    return JSON.stringify(filters);
  }

  clearCache(): void {
    this.cache.clear();
  }
}